import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AdminShell from './components/AdminShell';
import RoutesIndex from './RoutesIndex';
import { isAuthed } from './auth';
import { SettingsProvider } from './contexts/SettingsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SessionProvider } from './contexts/SessionContext';

// adjust this import path if your login page is elsewhere
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import CreateUser from './pages/CreateUser';
import UpdateUser from './pages/UpdateUser';
import UserCreated from './pages/UserCreated';
import SettingsPage from './pages/SettingsPage';
import CompanyManagement from './pages/CompanyManagement';
import UpdateCompany from './pages/UpdateCompany';
import WorkflowsPage from './pages/WorkflowsPage';
import AdminSetup from './pages/AdminSetup';
import UserSetup from './pages/UserSetup';
import PasswordReset from './pages/PasswordReset';
import WebPortal from './pages/WebPortal';
import SftpConnectors from './pages/SftpConnectors';
import Statistics from './pages/Statistics';
import Monitoring from './pages/Monitoring';
import As2TradingPartners from './pages/As2TradingPartners';
import WebPortalLogin from './pages/WebPortalLogin';
import Connector2Page from './pages/Connector2Page';
import SessionTestPage from './pages/SessionTestPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  console.log('🔐 RequireAuth check - isAuthed:', isAuthed());
  if (!isAuthed()) {
    console.log('❌ Not authenticated, redirecting to login');
    return <Navigate to='/login' replace />;
  }

  console.log('✅ Authenticated, rendering children');
  // Check if admin user needs to complete initial setup
  // This would typically check user attributes or a flag
  // For now, we'll let admin users access the portal and handle setup redirects within components

  return <>{children}</>;
}

function DomainRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  useEffect(() => {
    const hostname = window.location.hostname;
    const pathname = location.pathname;

    console.log('🔍 DomainRedirect:', { hostname, pathname });

    // Define domain types - check if hostname contains the domain pattern
    // Support both dev- and non-dev prefixes
    const isPasswordResetDomain =
      hostname.includes('pwdreset.nghs.com') ||
      hostname.includes('pwdreset.optimumcloudservices.com') ||
      hostname.includes('dev-pwdreset.nghs.com') ||
      hostname.includes('dev-pwdreset.optimumcloudservices.com');

    const isWebPortalDomain =
      hostname.includes('portal.nghs.com') ||
      hostname.includes('portal.optimumcloudservices.com') ||
      hostname.includes('dev-portal.nghs.com') ||
      hostname.includes('dev-portal.optimumcloudservices.com');

    const isAdminDomain =
      hostname.includes('transferadmin.nghs.com') ||
      hostname.includes('transferadmin.optimumcloudservices.com') ||
      hostname.includes('dev-transferadmin.nghs.com') ||
      hostname.includes('dev-transferadmin.optimumcloudservices.com');

    console.log('🔍 Domain types:', { isPasswordResetDomain, isWebPortalDomain, isAdminDomain });

    // Valid routes for each domain (removed /login from password reset and portal - it redirects above)
    const passwordResetRoutes = ['/password-reset', '/admin-setup', '/user-setup'];
    const webPortalRoutes = ['/web-portal-login', '/web-portal', '/admin-setup', '/user-setup'];
    const adminRoutes = ['/admin-dashboard', '/admin', '/login', '/admin-setup', '/user-setup', '/workflows', '/users', '/companies', '/sftp-connectors', '/statistics', '/monitoring', '/as2-trading-partners', '/settings'];

    // Admin-only routes that should be blocked on non-admin domains
    const adminOnlyRoutes = ['/admin-dashboard', '/admin', '/users', '/companies', '/sftp-connectors', '/statistics', '/monitoring', '/as2-trading-partners', '/settings', '/workflows'];

    // Check if current path is an admin-only route
    const isAdminRoute = adminOnlyRoutes.some(route => pathname === route || pathname.startsWith(route + '/'));

    // Redirect root path or /login based on domain
    if ((pathname === '/' || pathname === '/index.html' || pathname === '/login') && !hasRedirected.current) {
      console.log('🔍 Root/login path redirect check');
      if (isPasswordResetDomain) {
        console.log('✅ Redirecting to /password-reset');
        hasRedirected.current = true;
        navigate('/password-reset', { replace: true });
        return;
      } else if (isWebPortalDomain) {
        console.log('✅ Redirecting to /web-portal-login');
        hasRedirected.current = true;
        navigate('/web-portal-login', { replace: true });
        return;
      } else if (isAdminDomain) {
        // For admin domain, /login is valid, but root should go to dashboard
        if (pathname === '/' || pathname === '/index.html') {
          console.log('✅ Redirecting to /admin-dashboard');
          hasRedirected.current = true;
          navigate('/admin-dashboard', { replace: true });
          return;
        }
        // /login on admin domain is valid, don't redirect
        return;
      }
    }

    // Prevent access to admin routes from non-admin domains
    if (isAdminRoute && !isAdminDomain) {
      console.log('🚫 Blocking admin route on non-admin domain');
      if (isPasswordResetDomain) {
        console.log('✅ Redirecting to /password-reset');
        navigate('/password-reset', { replace: true });
        return;
      } else if (isWebPortalDomain) {
        console.log('✅ Redirecting to /web-portal-login');
        navigate('/web-portal-login', { replace: true });
        return;
      }
    }

    // Check if current route is valid for this domain
    const isValidRoute = 
      (isPasswordResetDomain && passwordResetRoutes.some(route => pathname === route || pathname.startsWith(route + '/'))) ||
      (isWebPortalDomain && webPortalRoutes.some(route => pathname === route || pathname.startsWith(route + '/'))) ||
      (isAdminDomain && adminRoutes.some(route => pathname === route || pathname.startsWith(route + '/'))) ||
      (!isPasswordResetDomain && !isWebPortalDomain && !isAdminDomain);

    console.log('🔍 Route validation:', { isValidRoute, pathname });

    // Redirect invalid routes to appropriate page for domain
    if (!isValidRoute && pathname !== '/' && pathname !== '/index.html') {
      console.log('🚫 Invalid route, redirecting');
      if (isPasswordResetDomain) {
        console.log('✅ Redirecting to /password-reset');
        navigate('/password-reset', { replace: true });
      } else if (isWebPortalDomain) {
        console.log('✅ Redirecting to /web-portal-login');
        navigate('/web-portal-login', { replace: true });
      } else if (isAdminDomain) {
        console.log('✅ Redirecting to /admin-dashboard');
        navigate('/admin-dashboard', { replace: true });
      } else {
        console.log('✅ Redirecting to /login');
        navigate('/login', { replace: true });
      }
    } else {
      console.log('✅ Route is valid, no redirect needed');
    }
  }, [navigate, location.pathname]);

  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <SettingsProvider>
        <SessionProvider>
          <DomainRedirect />
          <Routes>
            {/* Dev helper page index */}
            <Route path='/__pages' element={<RoutesIndex />} />

            {/* Public login */}
            <Route path='/login' element={<AdminLogin />} />

            {/* Setup pages */}
            <Route path='/admin-setup' element={<AdminSetup />} />
            <Route path='/user-setup' element={<UserSetup />} />

            {/* User-facing pages (no auth required) */}
            <Route path='/password-reset' element={<PasswordReset />} />
            <Route path='/web-portal-login' element={<WebPortalLogin />} />
            <Route path='/web-portal' element={<WebPortal />} />

            {/* Protected admin routes */}
            <Route
              path='/admin/*'
              element={
                <RequireAuth>
                  <AdminShell title='NGHS Transfer Portal'>
                    <Routes>
                      <Route path='/' element={<Navigate to='/admin-dashboard' replace />} />
                      <Route path='/admin-dashboard' element={<AdminDashboard />} />
                      <Route path='/users' element={<UserManagement />} />
                      <Route path='/users/create' element={<CreateUser />} />
                      <Route path='/users/update' element={<UpdateUser />} />
                      <Route path='/users/update/:id' element={<UpdateUser />} />
                      <Route path='/users/created' element={<UserCreated />} />
                      <Route path='/companies' element={<CompanyManagement />} />
                      <Route path='/companies/update/:companyId' element={<UpdateCompany />} />
                      <Route path='/sftp-connectors' element={<SftpConnectors />} />
                      <Route path='/statistics' element={<Statistics />} />
                      <Route path='/monitoring' element={<Monitoring />} />
                      <Route path='/as2-trading-partners' element={<As2TradingPartners />} />
                      <Route path='/settings' element={<SettingsPage />} />
                      <Route path='/workflows' element={<WorkflowsPage />} />
                      {/* Hidden development route - not in navigation */}
                      <Route path='/connector2-dev' element={<Connector2Page />} />
                      {/* Session management test page */}
                      <Route path='/session-test' element={<SessionTestPage />} />
                      {/* 404 inside the shell → go to dashboard */}
                      <Route path='*' element={<Navigate to='/' replace />} />
                    </Routes>
                  </AdminShell>
                </RequireAuth>
              }
            />

            {/* Root admin dashboard - protected */}
            <Route
              path='/admin-dashboard'
              element={
                <RequireAuth>
                  <AdminShell title='NGHS Transfer Portal'>
                    <AdminDashboard />
                  </AdminShell>
                </RequireAuth>
              }
            />

            {/* Direct workflows route - protected */}
            <Route
              path='/workflows'
              element={
                <RequireAuth>
                  <AdminShell title='NGHS Transfer Portal'>
                    <WorkflowsPage />
                  </AdminShell>
                </RequireAuth>
              }
            />

            {/* Root path - let domain redirect handle it */}
            <Route path='/' element={<div>Loading...</div>} />
          </Routes>
        </SessionProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
